import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/base/SEOHead';

export default function GuidesPage() {
  const [activeStep, setActiveStep] = useState(0);

  const steps = [
    {
      number: 1,
      title: 'Research Your Market & Define Your Business Idea',
      description: 'Understand El Paso\'s market demographics, identify your target customers, and validate your business concept with local data and insights.',
      tasks: [
        'Analyze El Paso market demographics and consumer behavior',
        'Identify your target customer segments',
        'Research competitors in the El Paso area',
        'Validate your business idea with potential customers',
        'Define your unique value proposition',
      ],
      resources: ['Market Research Tools', 'Demographics Data', 'Competitor Analysis Template'],
    },
    {
      number: 2,
      title: 'Create Your Business Plan',
      description: 'Develop a comprehensive business plan that outlines your strategy, financial projections, and operational approach.',
      tasks: [
        'Write executive summary and company description',
        'Conduct detailed market analysis',
        'Define your products or services',
        'Create marketing and sales strategy',
        'Develop financial projections and funding requirements',
      ],
      resources: ['Business Plan Template', 'Financial Projection Calculator', 'SWOT Analysis Guide'],
    },
    {
      number: 3,
      title: 'Choose Your Business Structure',
      description: 'Select the legal structure that best fits your business needs and register your entity with the appropriate authorities.',
      tasks: [
        'Compare business structures (LLC, Corporation, Sole Proprietorship)',
        'Consult with a business attorney or accountant',
        'Register with Texas Secretary of State',
        'Obtain Federal Employer Identification Number (EIN)',
        'Register for Texas state taxes',
      ],
      resources: ['Business Structure Guide', 'Registration Checklist', 'Legal Resources'],
    },
    {
      number: 4,
      title: 'Secure Funding',
      description: 'Explore funding options and secure the capital needed to launch and grow your business.',
      tasks: [
        'Calculate startup costs and capital requirements',
        'Explore small business loans and grants',
        'Consider bootstrapping or personal investment',
        'Prepare pitch deck for potential investors',
        'Apply for funding programs',
      ],
      resources: ['Funding Options Guide', 'Loan Application Checklist', 'Investor Network'],
    },
    {
      number: 5,
      title: 'Obtain Permits & Licenses',
      description: 'Navigate El Paso\'s regulatory requirements and obtain all necessary permits and licenses to operate legally.',
      tasks: [
        'Apply for City of El Paso Business License',
        'Obtain industry-specific permits and licenses',
        'Secure Certificate of Occupancy (if applicable)',
        'Register for sales tax permit',
        'Ensure zoning compliance',
      ],
      resources: ['Permit Checklist', 'Licensing Guide', 'Compliance Resources'],
    },
    {
      number: 6,
      title: 'Set Up Your Business Location',
      description: 'Find and prepare your business location, whether physical storefront or home office.',
      tasks: [
        'Research commercial real estate options in El Paso',
        'Negotiate lease terms',
        'Set up utilities and business services',
        'Design and furnish your space',
        'Install necessary equipment and technology',
      ],
      resources: ['Location Selection Guide', 'Lease Negotiation Tips', 'Setup Checklist'],
    },
    {
      number: 7,
      title: 'Build Your Brand & Online Presence',
      description: 'Create a strong brand identity and establish your digital presence to attract customers.',
      tasks: [
        'Design logo and brand identity',
        'Create professional website',
        'Set up social media profiles',
        'Develop content marketing strategy',
        'Implement SEO best practices',
      ],
      resources: ['Branding Guide', 'Website Builder Tools', 'Social Media Strategy'],
    },
    {
      number: 8,
      title: 'Launch & Grow Your Business',
      description: 'Execute your launch plan and implement strategies for sustainable growth.',
      tasks: [
        'Plan and execute launch event or campaign',
        'Implement marketing and sales strategies',
        'Build customer relationships',
        'Monitor financial performance',
        'Continuously improve and adapt',
      ],
      resources: ['Launch Checklist', 'Growth Strategies', 'Performance Metrics Guide'],
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Step-by-Step Guide to Starting a Business in El Paso, Texas"
        description="Complete roadmap for launching your El Paso business. Follow our 8-step guide covering market research, business planning, registration, funding, permits, location setup, branding, and growth strategies."
        canonical="/guides"
        keywords="how to start business El Paso, business startup guide Texas, El Paso business steps, launch business El Paso, startup roadmap Texas, business planning guide"
      />
      <Navbar />
      
      <main className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6">
          <header className="text-center mb-16">
            <h1 className="text-6xl font-bold text-gray-900 mb-6">Step-by-Step Guides</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Follow our comprehensive roadmap to launch your El Paso business successfully. Each step includes actionable tasks and helpful resources.
            </p>
          </header>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            <nav className="lg:col-span-1" aria-label="Guide steps navigation">
              <div className="bg-[#FAFAF8] rounded-2xl p-6 sticky top-24">
                <h2 className="text-lg font-bold text-gray-900 mb-4">Your Progress</h2>
                <div className="space-y-2">
                  {steps.map((step, index) => (
                    <button
                      key={index}
                      onClick={() => setActiveStep(index)}
                      className={`w-full text-left px-4 py-3 rounded-lg transition-all cursor-pointer whitespace-nowrap ${
                        activeStep === index
                          ? 'bg-[#D2691E] text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-100'
                      }`}
                      aria-current={activeStep === index ? 'step' : undefined}
                    >
                      <div className="flex items-center space-x-3">
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                            activeStep === index ? 'bg-white text-[#D2691E]' : 'bg-gray-200 text-gray-600'
                          }`}
                        >
                          {step.number}
                        </div>
                        <span className="text-sm font-medium">Step {step.number}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </nav>

            <section className="lg:col-span-3">
              <article className="bg-[#FAFAF8] rounded-2xl p-10">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-16 h-16 bg-[#D2691E] rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-3xl font-bold text-white">{steps[activeStep].number}</span>
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900">{steps[activeStep].title}</h2>
                </div>

                <p className="text-lg text-gray-600 leading-relaxed mb-8">
                  {steps[activeStep].description}
                </p>

                <div className="mb-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Tasks to Complete</h3>
                  <div className="space-y-3">
                    {steps[activeStep].tasks.map((task, index) => (
                      <div key={index} className="flex items-start space-x-3 bg-white rounded-lg p-4">
                        <div className="w-6 h-6 border-2 border-gray-300 rounded flex items-center justify-center flex-shrink-0 mt-0.5 cursor-pointer hover:border-[#D2691E] transition-colors">
                          <i className="ri-check-line text-transparent"></i>
                        </div>
                        <span className="text-sm text-gray-700 leading-relaxed">{task}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mb-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Helpful Resources</h3>
                  <div className="flex flex-wrap gap-3">
                    {steps[activeStep].resources.map((resource, index) => (
                      <button
                        key={index}
                        className="bg-white border border-gray-200 px-4 py-2 rounded-lg text-sm font-medium text-gray-700 hover:border-[#D2691E] hover:text-[#D2691E] transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-file-text-line mr-2"></i>
                        {resource}
                      </button>
                    ))}
                  </div>
                </div>

                <nav className="flex items-center justify-between pt-6 border-t border-gray-200" aria-label="Step navigation">
                  <button
                    onClick={() => setActiveStep(Math.max(0, activeStep - 1))}
                    disabled={activeStep === 0}
                    className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-semibold transition-all cursor-pointer whitespace-nowrap ${
                      activeStep === 0
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                    aria-label="Go to previous step"
                  >
                    <i className="ri-arrow-left-line"></i>
                    <span>Previous Step</span>
                  </button>
                  <button
                    onClick={() => setActiveStep(Math.min(steps.length - 1, activeStep + 1))}
                    disabled={activeStep === steps.length - 1}
                    className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-semibold transition-all cursor-pointer whitespace-nowrap ${
                      activeStep === steps.length - 1
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-[#D2691E] text-white hover:bg-[#B8571A]'
                    }`}
                    aria-label="Go to next step"
                  >
                    <span>Next Step</span>
                    <i className="ri-arrow-right-line"></i>
                  </button>
                </nav>
              </article>
            </section>
          </div>

          {/* UTEP Feature Card */}
          <aside className="mt-20">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Featured Resource</h2>
            <Link to="/guides/utep" className="block group cursor-pointer">
              <div className="relative rounded-2xl overflow-hidden h-[320px]">
                <img
                  src="https://public.readdy.ai/ai/img_res/0c0a6b2d4624b5e90490cdf0d3393aad.jpg"
                  alt="Aerial view of UTEP campus with Bhutanese architecture against Franklin Mountains"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <span className="inline-block bg-[#D2691E] text-white text-xs font-semibold px-3 py-1 rounded-full mb-3 whitespace-nowrap">University Guide</span>
                  <h3 className="text-2xl font-bold text-white mb-2">The University of Texas at El Paso (UTEP)</h3>
                  <p className="text-white/85 text-sm max-w-2xl leading-relaxed">
                    Explore UTEP&apos;s role as an economic powerhouse, its AACSB-accredited business programs, real estate opportunities near campus, and professional certificates for working professionals.
                  </p>
                </div>
              </div>
            </Link>
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  );
}